<template>
  <div class="legal">
    <!-- Hero -->
    <section class="legal__hero container">
      <p class="eyebrow reveal">Les règles du jeu</p>
      <h1 class="legal__title reveal" style="animation-delay:.05s">
        Conditions<br><span class="hl">d'utilisation</span>
      </h1>
      <p class="legal__lead reveal" style="animation-delay:.12s">
        En utilisant La Banane, tu acceptes ces quelques règles. Elles tiennent en
        une idée&nbsp;: qu'on rigole tous sans que ça dégénère.
      </p>
      <p class="legal__updated reveal" style="animation-delay:.16s">
        Dernière mise à jour&nbsp;: 10&nbsp;juillet&nbsp;2026
      </p>
    </section>

    <section class="container prose">
      <article class="block reveal">
        <h2>1. Objet du service</h2>
        <p>
          La Banane (labanane.net) est un site personnel et non commercial qui
          propose de consulter, voter et commenter des blagues en français, et de
          soumettre les siennes. L'usage du site vaut acceptation des présentes
          conditions générales d'utilisation (CGU). Si tu n'es pas d'accord avec
          elles, il te suffit de ne pas utiliser le service.
        </p>
      </article>

      <article class="block reveal">
        <h2>2. Création de compte</h2>
        <ul class="lst">
          <li>
            Certaines fonctionnalités (proposer une blague, commenter) nécessitent
            un compte, créé par e-mail/mot de passe ou via la connexion Google.
          </li>
          <li>
            Tu t'engages à fournir des informations exactes et à garder tes
            identifiants confidentiels. Tu es responsable de l'activité menée
            depuis ton compte.
          </li>
          <li>
            Un compte est <strong>personnel</strong>. Tu dois avoir la capacité
            légale de contracter&nbsp;; si tu es mineur, l'accord de tes
            représentants légaux est requis.
          </li>
        </ul>
      </article>

      <article class="block reveal">
        <h2>3. Règles de conduite</h2>
        <p>
          On aime l'humour large et un peu potache, mais il y a une frontière nette.
          En publiant sur le site (blague ou commentaire), tu t'engages à
          <strong>ne pas diffuser</strong> de contenu&nbsp;:
        </p>
        <ul class="lst">
          <li>appelant à la <strong>haine</strong>, à la violence ou à la discrimination envers une personne ou un groupe (origine, religion, sexe, orientation, handicap…)&nbsp;;</li>
          <li>constituant du <strong>harcèlement</strong>, des menaces ou des insultes visant une personne réelle identifiable&nbsp;;</li>
          <li><strong>illégal</strong> au regard du droit français (contenu pédopornographique, apologie du terrorisme ou de crimes, diffamation, atteinte à la vie privée…)&nbsp;;</li>
          <li>relevant du <strong>spam</strong>, de la publicité, de l'arnaque ou de la promotion de produits/services&nbsp;;</li>
          <li>violant les <strong>droits d'autrui</strong> (droit d'auteur, marques, données personnelles d'un tiers).</li>
        </ul>
        <p>
          En bref&nbsp;: une blague peut être lourde ou border-line, mais elle ne
          doit ni viser à nuire à quelqu'un de réel, ni être illégale. Pour notre
          état d'esprit sur le sujet, va lire
          <router-link class="ilink" to="/peut-on-rire-de-tout">«&nbsp;Peut-on rire de tout&nbsp;?&nbsp;»</router-link>.
        </p>
      </article>

      <article class="block reveal">
        <h2>4. Contenus que tu soumets</h2>
        <ul class="lst">
          <li>
            Tu restes <strong>l'auteur</strong> des contenus que tu publies. En les
            soumettant, tu accordes à La Banane le droit non exclusif et gratuit de
            les <strong>afficher, stocker et diffuser</strong> sur le site.
          </li>
          <li>
            Tu garantis disposer des droits nécessaires sur ce que tu publies et
            que ton contenu respecte les règles de l'article 3.
          </li>
          <li>
            Les blagues de l'époque de labanane.org sont conservées à titre de
            préservation patrimoniale (voir les
            <router-link class="ilink" to="/mentions-legales">mentions légales</router-link>).
          </li>
        </ul>
      </article>

      <div class="mod reveal">
        <h2>5. Modération</h2>
        <p>
          La Banane est un site modéré. Pour garder le contenu propre, cohérent et
          lisible, l'équipe se réserve le droit, <strong>à sa discrétion et sans
          préavis</strong>, de&nbsp;:
        </p>
        <ul class="lst lst--dark">
          <li><strong>réécrire ou retoucher</strong> une blague (orthographe, mise en forme, formulation)&nbsp;;</li>
          <li><strong>archiver</strong> un contenu (le retirer de l'affichage public sans le détruire)&nbsp;;</li>
          <li><strong>refuser ou supprimer</strong> tout contenu contraire aux présentes CGU&nbsp;;</li>
          <li><strong>suspendre ou clôturer</strong> un compte en cas d'abus répétés ou graves.</li>
        </ul>
        <p class="mod__note">
          La modération vise la qualité et la légalité du site, pas la censure d'un
          humour qui dérange. En cas de retrait qui te semble injuste, écris-nous à
          <a class="mail" href="mailto:contact@labanane.net">contact@labanane.net</a>.
        </p>
      </div>

      <article class="block reveal">
        <h2>6. Responsabilité</h2>
        <p>
          La Banane est un projet personnel fourni «&nbsp;en l'état&nbsp;», sans
          garantie de disponibilité continue ni d'absence d'erreur. L'éditeur ne
          saurait être tenu responsable&nbsp;:
        </p>
        <ul class="lst">
          <li>des contenus publiés par les utilisateurs, dont ceux-ci sont seuls responsables&nbsp;;</li>
          <li>d'une interruption, d'une perte de données ou d'un dysfonctionnement du service&nbsp;;</li>
          <li>de l'usage que tu fais des blagues en dehors du site.</li>
        </ul>
        <p>
          En tant qu'hébergeur des contenus utilisateurs, l'éditeur agit
          conformément à la LCEN&nbsp;: il retire promptement tout contenu
          manifestement illicite qui lui est signalé.
        </p>
      </article>

      <article class="block reveal">
        <h2>7. Résiliation</h2>
        <p>
          Tu peux supprimer ton compte à tout moment depuis ton
          <router-link class="ilink" to="/profil">profil</router-link>
          (rubrique «&nbsp;Zone de danger&nbsp;»), ou en écrivant à
          <a class="ilink" href="mailto:contact@labanane.net">contact@labanane.net</a>.
          L'éditeur peut suspendre ou fermer un compte en cas de non-respect des
          présentes CGU. Les conséquences sur tes données sont décrites dans la
          <router-link class="ilink" to="/confidentialite">politique de
          confidentialité</router-link>.
        </p>
      </article>

      <article class="block reveal">
        <h2>8. Évolution des CGU</h2>
        <p>
          Ces CGU peuvent évoluer. La date de dernière mise à jour figure en haut de
          page. En continuant à utiliser le site après une modification, tu acceptes
          la nouvelle version.
        </p>
      </article>

      <article class="block reveal">
        <h2>9. Droit applicable</h2>
        <p>
          Les présentes conditions sont régies par le <strong>droit français</strong>.
          En cas de litige, une solution amiable sera recherchée en priorité (écris
          à <a class="mail" href="mailto:contact@labanane.net">contact@labanane.net</a>)
          avant toute action&nbsp;; à défaut, les tribunaux français seront
          compétents.
        </p>
      </article>

      <p class="seealso reveal">
        Voir aussi&nbsp;:
        <router-link to="/confidentialite">Politique de confidentialité</router-link> ·
        <router-link to="/mentions-legales">Mentions légales</router-link>
      </p>
    </section>

    <section class="container legal__cta">
      <BananaButton to="/" size="lg" variant="dark">🍌 Retour aux blagues</BananaButton>
    </section>
  </div>
</template>

<script>
import BananaButton from '@/components/ui/BananaButton.vue'

export default {
  name: 'PageCgu',
  components: { BananaButton },
}
</script>

<style scoped>
.legal__hero { text-align: center; padding: 56px 20px 24px; }
.legal__title { font-size: clamp(2.4rem, 7vw, 4.4rem); }
.hl { color: var(--banana-700); }
.legal__lead {
  font-family: var(--font-accent);
  font-style: italic;
  font-size: clamp(1.15rem, 2.6vw, 1.5rem);
  color: var(--ink-700);
  max-width: 620px;
  margin: 10px auto 0;
}
.legal__updated {
  font-size: 0.85rem;
  color: var(--ink-500);
  margin: 14px auto 0;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.prose { display: flex; flex-direction: column; gap: 26px; padding: 20px 20px 10px; }

.block h2 { font-size: clamp(1.35rem, 3.4vw, 1.95rem); margin-bottom: 0.5em; }
.block p { font-size: 1.08rem; line-height: 1.7; color: var(--ink-800); margin: 0 0 0.9em; }
.block p:last-child { margin-bottom: 0; }
.block strong { color: var(--ink-900); }

.lst { list-style: none; margin: 0.4em 0 0.9em; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.lst li {
  position: relative;
  padding: 0 0 0 26px;
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--ink-800);
}
.lst li::before {
  content: "🍌";
  position: absolute;
  left: 0; top: 1px;
  font-size: 0.95rem;
}
.lst strong { color: var(--ink-900); }

.mail {
  color: var(--banana-800);
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-thickness: 2px;
  word-break: break-word;
}
.mail:hover { color: var(--banana-700); }
.ilink { color: var(--banana-800); font-weight: 600; text-decoration: underline; text-underline-offset: 3px; }
.ilink:hover { color: var(--banana-700); }

/* Moderation — dark highlight card */
.mod {
  background: var(--choco);
  color: var(--banana-100);
  border-radius: var(--r-lg);
  border: var(--border-w) solid var(--border-c);
  box-shadow: var(--shadow-lg);
  padding: 32px 32px 30px;
  margin: 8px 0;
}
.mod h2 { color: var(--banana-400); font-size: clamp(1.4rem, 3.6vw, 2rem); }
.mod p { font-size: 1.06rem; line-height: 1.7; opacity: 0.92; margin: 0 0 0.9em; }
.mod strong { color: var(--banana-300); }
.lst--dark li { color: var(--banana-100); opacity: 0.92; }
.lst--dark li::before { content: "•"; color: var(--banana-400); font-size: 1.2rem; top: -2px; }
.lst--dark strong { color: var(--banana-300); }
.mod__note { margin-bottom: 0 !important; opacity: 0.85; }
.mod__note .mail { color: var(--banana-300); }
.mod__note .mail:hover { color: var(--banana-200); }

.seealso {
  text-align: center;
  font-size: 0.95rem;
  color: var(--ink-500);
  margin: 6px 0 0;
}
.seealso a { color: var(--banana-800); font-weight: 600; text-decoration: underline; text-underline-offset: 3px; }
.seealso a:hover { color: var(--banana-700); }

.legal__cta { text-align: center; padding: 26px 20px 60px; }

@media (max-width: 620px) {
  .legal__hero { padding: 40px 4px 20px; }
  .prose { padding-left: 4px; padding-right: 4px; }
  .mod { padding: 26px 22px; }
}
</style>
